/**
 * Phase 3b lane additions — the handful of derivations and helpers the auth /
 * roster / profile screens need that `store/selectors.ts` does not already
 * expose. Kept in a separate module so the P3b lane never edits the shared
 * selector file while other lanes are working in it.
 *
 * Nothing here is a new *concept*: `pfSteamDot` is the one field the Profile
 * template reads off `ptVals()` (`pt.syncDot`, prototype line 764) rather than
 * its own view model, `copy()` is the prototype's clipboard helper (line 1688),
 * and `showDevLogins()` gates the prototype's dashed login footnote.
 *
 * When these are folded into `selectors.ts`, delete this file — the call sites
 * are Login/Crew/Profile/InvitePanel only.
 */

import { useQd } from './index.ts';
import type { QdStoreApi } from './index.ts';
import type { QdStore } from './types.ts';

// ---------------------------------------------------------------------------
// dev affordances
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    /** DEV only — see `installDevStoreHandle` below. */
    __qd?: QdStoreApi;
  }
}

/**
 * DEV-only store handle so a browser session can drive navigation without a
 * UI affordance (`window.__qd.getState().nav('profile')`). Never installed in a
 * production build: `import.meta.env.DEV` is statically false there and the
 * whole block is dropped.
 */
if (import.meta.env.DEV && typeof window !== 'undefined') window.__qd = useQd;

/**
 * The prototype's dashed "PROTOTYPE LOGINS" footnote (template line 63) only
 * makes sense against the seeded `LocalRepository` in a dev build. In Supabase
 * mode — or any production build — it must never render: there is no shared
 * password to advertise.
 */
export function showDevLogins(): boolean {
  return !!import.meta.env.DEV && import.meta.env.VITE_REPOSITORY === 'local';
}

// ---------------------------------------------------------------------------
// profile
// ---------------------------------------------------------------------------

/**
 * Status dot beside `pfSteamTxt` on the Profile Steam panel. The template reads
 * `pt.syncDot` (line 764), which `playtestsVM()` computes — but running the
 * whole playtests view model for one colour would walk every tracked test, so
 * the one-line rule is repeated here verbatim (`selectors.ts` line 847).
 */
export function pfSteamDot(s: QdStore): string {
  return s.steamLink && s.steamLink.linked ? 'var(--acc)' : 'var(--acc2)';
}

// ---------------------------------------------------------------------------
// admin / invites
// ---------------------------------------------------------------------------

/**
 * PORTING-NOTES D-03. The temp code is displayed exactly once, in the invite
 * panel; `COPY INVITE` on an admin row afterwards can only carry identity
 * (`crewVM().copyValue`), so the row says so.
 */
export const INVITE_CODE_NOTE = 'CODE SHOWN ONCE — HIT RESET PW FOR A NEW ONE';

/**
 * The Profile ▸ STEAM ACCOUNT caption, on both trees.
 *
 * The mirror's `POWERS “GOT IN” + “REQUESTED” BADGES ON THE PLAYTESTS TAB`
 * overstated the link by one badge — see `selectors.3e#PT_SYNC_NOTE` for the
 * argument. One constant rather than the same sentence typed into
 * `screens/Profile.tsx` and `desktop/screens/Profile.tsx`, which is how the two
 * copies of it came to be corrected separately.
 */
export const PF_STEAM_CAP =
  'POWERS THE “GOT IN” BADGE ON THE PLAYTESTS TAB — REQUESTS ARE SELF-REPORTED';

/**
 * Prototype `copy(txt, msg)` (line 1688): fire-and-forget clipboard write that
 * toasts on both settle paths, because a rejected `writeText` (permissions,
 * insecure origin) still means the user tapped the button and deserves an
 * answer. `navigator.clipboard` is absent in non-secure contexts, hence the
 * optional access rather than a bare call.
 */
export function copy(text: string, msg?: string): void {
  const done = (): void => useQd.getState().toast(msg ?? 'COPIED TO CLIPBOARD');
  try {
    const cb = navigator.clipboard;
    if (!cb) {
      done();
      return;
    }
    void cb.writeText(text).then(done, done);
  } catch {
    done();
  }
}
